import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import heic2any from 'heic2any';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import Spinner from '../../components/Spinner';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import UniversitySearch from '../../components/UniversitySearch/UniversitySearch';
import { SUPPORTED_UNIVERSITY_IDS } from '../../config/supportedUniversities';
import './Edit.css';

interface FormData {
    title: string;
    description: string;
    unit_type: string;
    address: string;
    monthly_rent: string;
    pictures: string[];
    lease_term: string[];
    university_id: string;
    parking: boolean;
    allow_pets: boolean;
    gym: boolean;
    pool: boolean;
    laundry: boolean;
    gender_preference: string;
    facebook_url?: string;
    instagram_username?: string;
}

const Edit: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { isAuthenticated, user } = useAuth();
    const { isDarkMode } = useTheme();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);
    const [uploadedImagePreviews, setUploadedImagePreviews] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<string[]>([]);
    const [primaryImageIndex, setPrimaryImageIndex] = useState<number>(0);
    const [listing, setListing] = useState<any>(null);

    const [formData, setFormData] = useState<FormData>({
        title: '',
        description: '',
        unit_type: '',
        address: '',
        monthly_rent: '',
        pictures: [],
        lease_term: [],
        university_id: '',
        parking: false,
        allow_pets: false,
        gym: false,
        pool: false,
        laundry: false,
        gender_preference: 'no_preference',
        facebook_url: '',
        instagram_username: ''
    });

    const fetchListing = useCallback(async () => {
        if (!id) return;

        try {
            const authToken = localStorage.getItem('auth_token');
            if (!authToken) {
                setError('Please log in to edit your listing');
                setInitialLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/api/v1/subleases/${id}`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.data) {
                const listingData = response.data.data || response.data;
                setListing(listingData);

                // Parse lease_term - it might be a string (comma-separated) or array
                let leaseTerms: string[] = [];
                if (listingData.lease_term) {
                    if (Array.isArray(listingData.lease_term)) {
                        leaseTerms = listingData.lease_term;
                    } else if (typeof listingData.lease_term === 'string') {
                        leaseTerms = listingData.lease_term.split(',').map((t: string) => t.trim()).filter((t: string) => t);
                    }
                }

                // Populate form with existing listing data
                setFormData({
                    title: listingData.title || '',
                    description: listingData.description || '',
                    unit_type: listingData.unit_type || '',
                    address: listingData.address || '',
                    monthly_rent: listingData.monthly_rent?.toString() || '',
                    pictures: listingData.pictures || [],
                    lease_term: leaseTerms,
                    university_id: (listingData.university_id && listingData.university_id !== 0) ? listingData.university_id.toString() : '',
                    parking: listingData.parking === 'true' || listingData.parking === true,
                    allow_pets: listingData.allow_pets === 'true' || listingData.allow_pets === true,
                    gym: listingData.gym === 'true' || listingData.gym === true,
                    pool: listingData.pool === 'true' || listingData.pool === true,
                    laundry: listingData.laundry === 'true' || listingData.laundry === true,
                    gender_preference: listingData.gender_preference || 'no_preference',
                    facebook_url: listingData.facebook_url || '',
                    instagram_username: listingData.instagram_username || ''
                });
                setExistingImages(listingData.pictures || []);
            }
        } catch (error: any) {
            console.error('Error fetching listing:', error);
            if (error.response?.status === 404) {
                setError('Listing not found');
            } else if (error.response?.status === 401) {
                setError('Please log in to edit your listing');
            } else {
                setError('Failed to load listing data');
            }
        } finally {
            setInitialLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchListing();
        }
    }, [id, fetchListing]);

    // Cleanup object URLs when component unmounts or images change
    useEffect(() => {
        return () => {
            uploadedImagePreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [uploadedImagePreviews]);

    // Auto-resize description textarea
    useEffect(() => {
        const textarea = document.getElementById('description') as HTMLTextAreaElement;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.max(100, textarea.scrollHeight)}px`;
        }
    }, [formData.description]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            return newData;
        });
    };

    const handleLeaseTermChange = (term: string) => {
        setFormData(prev => {
            const currentTerms = prev.lease_term;
            const isSelected = currentTerms.includes(term);

            if (isSelected) {
                // Remove term if already selected
                return {
                    ...prev,
                    lease_term: currentTerms.filter(t => t !== term)
                };
            } else {
                // Add term if not selected
                return {
                    ...prev,
                    lease_term: [...currentTerms, term]
                };
            }
        });
    };

    const compressImage = async (file: File, maxSizeKB: number = 500): Promise<File> => {
        let fileToProcess = file;
        
        // Convert HEIC/HEIF to JPEG
        const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                       file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
        
        if (isHeic) {
            try {
                const convertedBlob = await heic2any({
                    blob: file,
                    toType: 'image/jpeg',
                    quality: 0.8
                });
                const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                fileToProcess = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                });
            } catch (error) {
                console.error("Error converting HEIC:", error);
                // Fall back to original file if conversion fails
            }
        }

        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions (max 1920x1080)
                let { width, height } = img;
                const maxWidth = 1920;
                const maxHeight = 1080;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;

                // Draw and compress
                ctx?.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const compressedFile = new File([blob], fileToProcess.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });
                        resolve(compressedFile);
                    } else {
                        resolve(fileToProcess);
                    }
                }, 'image/jpeg', 0.8); // 80% quality
            };

            img.onerror = () => {
                // If image fails to load (e.g., HEIC on unsupported browsers), just resolve with original
                console.warn(`Failed to decode image for compression: ${fileToProcess.name}. Proceeding with original file.`);
                resolve(fileToProcess);
            };

            img.src = URL.createObjectURL(fileToProcess);
        });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);

        // Detect mobile device for better error messages
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        const totalImages = existingImages.length + uploadedImages.length + files.length;
        if (totalImages > 20) {
            setError('Maximum 20 photos allowed');
            return;
        }

        // Validate file types - be more lenient for mobile devices
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/tiff', 'image/bmp', 'image/gif'];
        const invalidFiles = files.filter(file => {
            // For mobile devices, also check file extension if MIME type is not recognized
            if (!validTypes.includes(file.type)) {
                if (isMobile && file.name && file.name.includes('.')) {
                    const ext = file.name.split('.').pop()?.toLowerCase();
                    const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'tiff', 'bmp', 'gif'];
                    return !validExtensions.includes(ext || '');
                }
                return true;
            }
            return false;
        });

        if (invalidFiles.length > 0) {
            console.error('Invalid file types:', invalidFiles.map(f => ({ name: f.name, type: f.type })));
            const errorMessage = isMobile
                ? 'Please select valid image files. Supported formats: JPEG, PNG, WebP, HEIC, HEIF, TIFF, BMP, GIF'
                : 'Only JPEG, PNG, WebP, HEIC, HEIF, TIFF, and BMP images are allowed';
            setError(errorMessage);
            return;
        }

        // Validate file sizes (max 10MB per file before processing)
        const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            console.error('Oversized files:', oversizedFiles.map(f => ({ name: f.name, size: f.size })));
            setError('Each image must be less than 10MB');
            return;
        }

        // Additional validation for empty files
        const emptyFiles = files.filter(file => file.size === 0);
        if (emptyFiles.length > 0) {
            console.error('Empty files detected:', emptyFiles.map(f => ({ name: f.name, size: f.size })));
            setError('One or more files appear to be empty. Please select valid image files.');
            return;
        }

        // Compress images before adding to state
        try {
            setError('Compressing images...');
            const compressedFiles: File[] = [];
            for (const file of files) {
                try {
                    const compressed = await compressImage(file);
                    compressedFiles.push(compressed);
                } catch (err) {
                    console.error('Error compressing a file:', err);
                    compressedFiles.push(file); // Fallback to original
                }
            }

            console.log('Original files:', files.map(f => ({ name: f.name, size: f.size })));
            console.log('Compressed files:', compressedFiles.map(f => ({ name: f.name, size: f.size })));

            setUploadedImages(prev => [...prev, ...compressedFiles]);
            setUploadedImagePreviews(prev => [
                ...prev,
                ...compressedFiles.map((file) => URL.createObjectURL(file)),
            ]);
            setError('');
        } catch (error) {
            console.error('Image compression error:', error);
            setError('Failed to process images. Please try again.');
            return;
        }

        // Reset the file input to prevent issues on Windows
        e.target.value = '';
    };

    const removeExistingImage = (index: number) => {
        setExistingImages(prev => prev.filter((_, i) => i !== index));
        // Adjust primary image index
        if (primaryImageIndex === index) {
            setPrimaryImageIndex(0);
        } else if (primaryImageIndex > index) {
            setPrimaryImageIndex(primaryImageIndex - 1);
        }
    };

    const removeNewImage = (index: number) => {
        setUploadedImagePreviews(prev => {
            const urlToRevoke = prev[index];
            if (urlToRevoke) {
                URL.revokeObjectURL(urlToRevoke);
            }
            return prev.filter((_, i) => i !== index);
        });
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
        // Adjust primary image index (global indices: existing first, then uploaded)
        const globalIndex = existingImages.length + index;
        if (primaryImageIndex === globalIndex) {
            setPrimaryImageIndex(0);
        } else if (primaryImageIndex > globalIndex) {
            setPrimaryImageIndex(primaryImageIndex - 1);
        }
    };

    const setPrimaryImage = (index: number) => {
        setPrimaryImageIndex(index);
    };

    const uploadImagesToServer = async (images: File[]): Promise<string[]> => {
        if (images.length === 0) return [];

        try {
            // Convert images to base64 (same logic as profile upload)
            console.log('Converting images to base64...');
            const base64Images = await Promise.all(
                images.map(async (image) => {
                    const base64Data = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const result = reader.result as string;
                            resolve(result);
                        };
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(image);
                    });

                    return {
                        filename: image.name,
                        content_type: image.type,
                        image_data: base64Data
                    };
                })
            );

            const authToken = localStorage.getItem('auth_token');
            console.log('Uploading to base64 endpoint sequentially...');
            const uploadedUrls: string[] = [];

            for (let i = 0; i < base64Images.length; i++) {
                console.log(`Uploading image ${i + 1} of ${base64Images.length}...`);
                const response = await fetch(API_ENDPOINTS.UPLOAD.MULTIPLE_BASE64, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        images: [base64Images[i]]
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || `Upload failed with status ${response.status}`);
                }

                const data = await response.json();
                if (data && data.uploaded_files && Array.isArray(data.uploaded_files) && data.uploaded_files.length > 0) {
                    uploadedUrls.push(data.uploaded_files[0].url);
                } else {
                    throw new Error('Invalid response format from upload service');
                }
            }

            return uploadedUrls;
        } catch (error: any) {
            console.error('Image upload error:', error);

            if (error.code === 'ECONNABORTED') {
                throw new Error('Upload timed out. Please try with smaller images or check your connection.');
            }

            if (error.response?.status === 401) {
                throw new Error('Authentication failed. Please log in again.');
            }

            if (error.response?.status === 413) {
                throw new Error('Images are too large. Please compress them and try again.');
            }

            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }

            throw new Error('Failed to upload images. Please check your connection and try again.');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isAuthenticated) {
            setError('Please log in to edit your listing');
            return;
        }

        if (!id) {
            setError('Invalid listing ID');
            return;
        }

        const totalImages = existingImages.length + uploadedImages.length;
        if (totalImages === 0) {
            setError('Please upload at least one photo');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Upload new images if any
            let newImageUrls: string[] = [];
            if (uploadedImages.length > 0) {
                try {
                    newImageUrls = await uploadImagesToServer(uploadedImages);
                } catch (uploadError: any) {
                    setError(`Image upload failed: ${uploadError.message || 'Unknown error'}`);
                    setLoading(false);
                    return;
                }
            }

            // Combine existing and new images
            const allImages = [...existingImages, ...newImageUrls];

            // Reorder images so primary image is first if needed
            const reorderedImages = [...allImages];
            if (primaryImageIndex > 0 && primaryImageIndex < allImages.length) {
                const primaryImage = reorderedImages[primaryImageIndex];
                reorderedImages.splice(primaryImageIndex, 1);
                reorderedImages.unshift(primaryImage);
            }

            const updateData = {
                title: formData.title,
                description: formData.description,
                unit_type: formData.unit_type,
                address: formData.address,
                monthly_rent: formData.monthly_rent, // Keep as string for ListingUpdate model
                lease_term: 'summer', // Hardcoded as requested
                university_id: formData.university_id ? parseInt(formData.university_id) : 0,
                parking: formData.parking ? "true" : "false",
                allow_pets: formData.allow_pets ? "true" : "false",
                gym: formData.gym ? "true" : "false",
                pool: formData.pool ? "true" : "false",
                laundry: formData.laundry ? "true" : "false",
                gender_preference: formData.gender_preference,
                facebook_url: formData.facebook_url?.trim() || null,
                instagram_username: formData.instagram_username?.trim() || null,
                pictures: reorderedImages, // Include both existing and new images
            };

            const authToken = localStorage.getItem('auth_token');
            const response = await axios.patch(
                `${API_BASE_URL}/api/v1/subleases/${id}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 200) {
                setSuccess(true);
                setTimeout(() => {
                    navigate(`/listing/${id}`);
                }, 1500);
            }
        } catch (error: any) {
            console.error('Error updating listing:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);

            let errorMessage = 'Failed to update listing. Please try again.';

            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                // Handle array of validation errors (422 from FastAPI)
                if (Array.isArray(detail)) {
                    // Extract the first error message
                    const firstError = detail[0];
                    if (typeof firstError === 'string') {
                        errorMessage = firstError;
                    } else if (firstError?.msg) {
                        errorMessage = firstError.msg;
                    } else {
                        errorMessage = 'Invalid data provided. Please check your form inputs.';
                    }
                } else if (typeof detail === 'string') {
                    errorMessage = detail;
                } else if (detail?.msg) {
                    errorMessage = detail.msg;
                } else {
                    errorMessage = 'Invalid data provided. Please check your form inputs.';
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else if (error.response?.status === 401) {
                errorMessage = 'Authentication failed. Please log in again.';
            } else if (error.response?.status === 403) {
                errorMessage = 'You do not have permission to update this listing.';
            } else if (error.response?.status === 422) {
                errorMessage = 'Invalid data provided. Please check your form inputs.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error. Please try again later.';
            } else if (!error.response) {
                errorMessage = 'Unable to connect to server. Please check your internet connection.';
            }

            setError(String(errorMessage));
        } finally {
            setLoading(false);
        }
    };

    // Check if user is authenticated
    const isUserAuthenticated = isAuthenticated && user && localStorage.getItem('auth_token');

    if (initialLoading) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`edit-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="edit-container">
                        <div className="loading">
                            <div className="edit-dots-loader" aria-label="Loading" role="status">
                                <span className="edit-dots-loader-dot" />
                                <span className="edit-dots-loader-dot" />
                                <span className="edit-dots-loader-dot" />
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!isUserAuthenticated) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`edit-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="edit-container">
                        <div className="error-message">
                            ❌ You must be logged in to edit your listing. Please log in and try again.
                        </div>
                        <button className="back-btn" onClick={() => navigate('/')}>
                            Go to Home
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!listing) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`edit-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="edit-container">
                        <div className="error-message">
                            ❌ {error || 'Listing not found'}
                        </div>
                        <button className="back-btn" onClick={() => navigate('/profile')}>
                            Back to Profile
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const totalImages = existingImages.length + uploadedImages.length;
    const primaryDisplayIndex = Math.min(Math.max(primaryImageIndex, 0), Math.max(0, totalImages - 1));

    return (
        <>
            <NavBar inverted={true} />
            <div className={`edit-page ${isDarkMode ? 'dark-mode' : ''}`}>
                <div className="edit-container">
                    <div className="edit-header">
                        <h1>Edit Listing</h1>
                    </div>

                    {success && (
                        <div className="success-message">
                            ✅ Listing updated successfully! Redirecting to your listing...
                        </div>
                    )}

                    {error && (
                        <div className="error-message">
                            ❌ {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="edit-form">
                        <div className="form-group">
                            <label htmlFor="title">Title</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                maxLength={30}
                                placeholder="e.g., Room at the Standard, Room at Urban Place Aparments."
                            />
                            <div className={`character-count ${formData.title.length > 30 ? 'error' :
                                formData.title.length > 25 ? 'warning' : ''
                                }`}>
                                {formData.title.length}/30 characters
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                maxLength={2000}
                                rows={4}
                                placeholder="Describe your listing..."
                                style={{
                                    minHeight: '100px',
                                    resize: 'none',
                                    overflow: 'hidden'
                                }}
                                onInput={(e) => {
                                    const target = e.target as HTMLTextAreaElement;
                                    target.style.height = 'auto';
                                    target.style.height = `${Math.max(100, target.scrollHeight)}px`;
                                }}
                            />
                            <div className={`character-count ${formData.description.length > 2000 ? 'error' :
                                formData.description.length > 1800 ? 'warning' : ''
                                }`}>
                                {formData.description.length}/2000 characters
                            </div>
                        </div>


                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="unit_type">Entire Unit Type</label>
                                <select
                                    id="unit_type"
                                    name="unit_type"
                                    value={formData.unit_type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select unit type</option>
                                    <option value="studio">Studio</option>
                                    <option value="1x1">1x1</option>
                                    <option value="1x2">1x2</option>
                                    <option value="2x1">2x1</option>
                                    <option value="2x2">2x2</option>
                                    <option value="3x1">3x1</option>
                                    <option value="3x2">3x2</option>
                                    <option value="3x3">3x3</option>
                                    <option value="4x2">4x2</option>
                                    <option value="4x3">4x3</option>
                                    <option value="4x4">4x4</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="monthly_rent">Monthly Rent ($)</label>
                                <input
                                    type="number"
                                    id="monthly_rent"
                                    name="monthly_rent"
                                    value={formData.monthly_rent}
                                    onChange={handleInputChange}
                                    required
                                    min="1"
                                    placeholder="800"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="address">Address</label>
                            <AddressAutocomplete
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={(value) => setFormData(prev => ({ ...prev, address: value }))}
                                required
                                maxLength={300}
                                placeholder="Enter Address ..."
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="university_id">University (Optional)</label>
                            <UniversitySearch
                                value={formData.university_id}
                                onChange={(value) => setFormData(prev => ({ ...prev, university_id: value }))}
                                placeholder="Search University e.g. USF (Optional)"
                                className="edit-page-university-search"
                                disableCapsuleClick={true}
                                supportedUniversityIds={SUPPORTED_UNIVERSITY_IDS}
                                disableCapsule={true}
                                showCities={false}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="facebook_url">Facebook Link (Optional)</label>
                            <input
                                type="url"
                                id="facebook_url"
                                name="facebook_url"
                                value={formData.facebook_url || ''}
                                onChange={handleInputChange}
                                placeholder="e.g. https://www.facebook.com/marketplace/item/12345"
                                maxLength={500}
                            />
                        </div>

                        {(!user?.instagram || formData.instagram_username) && (
                            <div className="form-group">
                                <label htmlFor="instagram_username">Instagram Username (Optional)</label>
                                <input
                                    type="text"
                                    id="instagram_username"
                                    name="instagram_username"
                                    value={formData.instagram_username || ''}
                                    onChange={handleInputChange}
                                    placeholder="e.g. usublease"
                                    maxLength={50}
                                />
                            </div>
                        )}

                        <div className="form-group utilities-section">
                            <label className="section-label">Utilities</label>
                            <div className="utilities-checkboxes">
                                {/* Row 1: Parking & Laundry */}
                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="parking"
                                            checked={formData.parking}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Parking</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="laundry"
                                            checked={formData.laundry}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Laundry</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="allow_pets"
                                            checked={formData.allow_pets}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Pets</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="pool"
                                            checked={formData.pool}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Pool</span>
                                    </label>
                                </div>

                                <div className="checkbox-group">
                                    <label className="utility-capsule">
                                        <input
                                            type="checkbox"
                                            name="gym"
                                            checked={formData.gym}
                                            onChange={handleInputChange}
                                            className="utility-capsule-input"
                                        />
                                        <span className="utility-capsule-text">Gym</span>
                                    </label>
                                </div>
                            </div>

                            <div className="gender-preference-section">
                                <label htmlFor="gender_preference" className="gender-label">Gender Preference</label>
                                <select
                                    id="gender_preference"
                                    name="gender_preference"
                                    value={formData.gender_preference}
                                    onChange={handleInputChange}
                                    className="gender-select"
                                >
                                    <option value="no_preference">No Gender Preference</option>
                                    <option value="male_preferred">Males</option>
                                    <option value="female_preferred">Females</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group photo-upload-section">
                            <label>Photos (Max 20)</label>
                            <p className="photo-instruction">Please ensure the first photo is of the room and NOT the layout.</p>
                            <div className="photo-upload-area">
                                <input
                                    type="file"
                                    id="photo-upload-edit"
                                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/tiff,image/bmp,image/gif"
                                    multiple
                                    onChange={handleImageUpload}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="photo-upload-edit" className="upload-button">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Add Photos
                                </label>
                            </div>

                            {totalImages > 0 && (
                                <div className="uploaded-images">
                                    <div className="images-grid">
                                        {(() => {
                                            const items: Array<{
                                                key: string;
                                                globalIndex: number;
                                                src: string;
                                                alt: string;
                                                onRemove: (e: React.MouseEvent) => void;
                                            }> = [];

                                            existingImages.forEach((image, index) => {
                                                items.push({
                                                    key: `existing-${index}`,
                                                    globalIndex: index,
                                                    src: image.startsWith('http') ? image : `${API_BASE_URL}${image}`,
                                                    alt: `Existing ${index + 1}`,
                                                    onRemove: (e) => {
                                                        e.stopPropagation();
                                                        removeExistingImage(index);
                                                    },
                                                });
                                            });

                                            uploadedImages.forEach((_, index) => {
                                                const globalIndex = existingImages.length + index;
                                                items.push({
                                                    key: `new-${index}`,
                                                    globalIndex,
                                                    src: uploadedImagePreviews[index],
                                                    alt: `New ${index + 1}`,
                                                    onRemove: (e) => {
                                                        e.stopPropagation();
                                                        removeNewImage(index);
                                                    },
                                                });
                                            });

                                            return items.map((item) => (
                                                <div
                                                    key={item.key}
                                                    className="image-preview"
                                                    onClick={() => setPrimaryImage(item.globalIndex)}
                                                    title="Click to set as primary photo"
                                                >
                                                    {item.globalIndex === primaryDisplayIndex && (
                                                        <div className="primary-tile-label" aria-hidden="true">Primary</div>
                                                    )}
                                                    <img src={item.src} alt={item.alt} />
                                                    {item.globalIndex !== primaryDisplayIndex && (
                                                        <div className="image-controls">
                                                            <button
                                                                type="button"
                                                                className="remove-btn"
                                                                onClick={item.onRemove}
                                                                title="Remove photo"
                                                                aria-label="Remove photo"
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                    <p className="photos-count">{totalImages}/20 photos uploaded</p>
                                </div>
                            )}
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="small" color="white" />
                                        Updating Listing...
                                    </>
                                ) : (
                                    'Update Listing'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Edit;
